import type { KitViolation, MasterPlan, PlanKit, PlanOrder } from "@patronix/domain";
import { addDays, mondayOf } from "@patronix/domain/aps-rules";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ChevronLeft, ChevronRight, Truck } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DetailPanel } from "@/components/DataList";
import { primaryButtonClass, secondaryButtonClass, ServerError } from "@/components/Panel";
import { buildRows, ComponentStrip, LABEL_W, LoadStrip, MasterPlanBoard, type Selection } from "@/features/planning/MasterPlanBoard";
import { masterPlanKeys, masterPlanQuery, planComponentsQuery, workCenterLoadQuery, type PlanWindow } from "@/features/planning/masterPlan";
import { PlanningExamples } from "@/features/planning/PlanningExamples";
import { RunBar } from "@/features/planning/RunBar";
import { KitPanel } from "@/features/production/KitPanel";
import { kitErrorMessage, kitKeys, moveOrder, shiftKit } from "@/features/production/kits";
import { ProductionOrderPanel } from "@/features/production/ProductionOrderPanel";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { todayIso, useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";

// Master plan (APS P2, docs/design/aps.md §6, K13): the planner moves kits on
// the time axis — one drag shifts every order of the kit, the database
// re-dates them in one transaction — and sees on the same axis whether the
// materials (bar colours, ◆ CTP, component strip) and the work centers
// (load strip) allow it. First render: 2 RPCs (plan + load); the component
// strip adds one when shown. A move = 1 RPC (optimistic, rolled back on
// error) + background refetches.

type Zoom = "day" | "week";
type Strip = "load" | "components";
interface Search {
  from: string;
  zoom: Zoom;
  strip: Strip;
  kit?: string | undefined;
  order?: string | undefined;
}

const SPAN: Record<Zoom, { days: number; px: number; step: number }> = {
  day: { days: 28, px: 36, step: 7 },
  week: { days: 84, px: 13, step: 28 },
};
const ISO = /^\d{4}-\d{2}-\d{2}$/;

function windowOf(companyId: string, s: Pick<Search, "from" | "zoom">): PlanWindow {
  return { companyId, from: s.from, to: addDays(s.from, SPAN[s.zoom].days - 1) };
}

export const Route = createFileRoute("/_app/planning/master-plan")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      from: typeof raw.from === "string" && ISO.test(raw.from) ? mondayOf(raw.from) : mondayOf(todayIso()),
      zoom: raw.zoom === "week" ? "week" : "day",
      strip: raw.strip === "components" ? "components" : "load",
    };
    if (typeof raw.kit === "string" && raw.kit) s.kit = raw.kit;
    if (typeof raw.order === "string" && raw.order) s.order = raw.order;
    return s;
  },
  loaderDeps: ({ search }) => ({ from: search.from, zoom: search.zoom }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (!companyId) return;
    const w = windowOf(companyId, deps);
    await Promise.all([context.queryClient.ensureQueryData(masterPlanQuery(w)), context.queryClient.ensureQueryData(workCenterLoadQuery(w))]);
  },
  component: MasterPlanPage,
});

/** The plan with a kit or a subtree moved by `days` (optimistic update). */
function shifted(plan: MasterPlan, ids: ReadonlySet<string>, days: number, kitId: string | null): MasterPlan {
  const move = (o: PlanOrder): PlanOrder =>
    ids.has(o.id) && o.movable ? { ...o, start_date: addDays(o.start_date, days), due_date: addDays(o.due_date, days) } : o;
  return {
    ...plan,
    kits: plan.kits.map((k) => {
      if (!k.orders.some((o) => ids.has(o.id)) && k.id !== kitId) return k;
      const orders = k.orders.map(move);
      const open = orders.filter((o) => o.status === "planned" || o.status === "released" || o.status === "in_progress");
      const due = open.reduce<string | null>((m, o) => (!m || o.due_date > m ? o.due_date : m), null);
      return { ...k, orders, planned_start: k.id === kitId ? addDays(k.planned_start, days) : k.planned_start, planned_due: due };
    }),
    loose: plan.loose.map(move),
  };
}

function MasterPlanPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canMove = useCan("production.order.manage");
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const span = SPAN[search.zoom];
  // The day width stretches so the window fills the board on wide screens
  // (never narrower than the zoom's own width); the window stays the same.
  const boardBoxRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(0);
  useLayoutEffect(() => {
    const el = boardBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setBoardWidth(entry?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const px = Math.max(span.px, Math.floor((boardWidth - LABEL_W - 4) / span.days));
  const w = windowOf(company?.id ?? "", search);
  const planQ = useQuery(masterPlanQuery(w));
  const loadQ = useQuery(workCenterLoadQuery(w));
  const compQ = useQuery(planComponentsQuery(w, search.strip === "components"));
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [comp, setComp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ order: PlanOrder; days: number; violations: KitViolation[] } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  const plan = planQ.data;
  const rows = useMemo(() => buildRows(plan, collapsed), [plan, collapsed]);
  const highlight = useMemo(() => {
    if (!comp) return null;
    const c = compQ.data?.components.find((x) => `${x.item_id}:${x.project_code ?? ""}` === comp);
    return c ? new Set(c.kit_ids) : null;
  }, [comp, compQ.data]);

  // Keep the board and the strip on the same horizontal position.
  const syncing = useRef(false);
  const syncTo = useCallback((target: "board" | "strip", x: number) => {
    const el = target === "board" ? boardRef.current : stripRef.current;
    if (syncing.current || !el) return;
    syncing.current = true;
    el.scrollLeft = x;
    requestAnimationFrame(() => (syncing.current = false));
  }, []);
  const toStrip = useCallback((x: number) => syncTo("strip", x), [syncTo]);
  const toBoard = useCallback((x: number) => syncTo("board", x), [syncTo]);
  const clearTransforms = () =>
    requestAnimationFrame(() => boardRef.current?.querySelectorAll<HTMLElement>("[data-kit-bar], [data-order]").forEach((el) => (el.style.transform = "")));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: masterPlanKeys.all });
    void queryClient.invalidateQueries({ queryKey: kitKeys.all });
    void queryClient.invalidateQueries({ queryKey: ["planning"] });
  };
  const optimistic = (ids: ReadonlySet<string>, days: number, kitId: string | null) => {
    const key = masterPlanKeys.plan(w);
    const before = queryClient.getQueryData<MasterPlan>(key);
    if (before) queryClient.setQueryData(key, shifted(before, ids, days, kitId));
    clearTransforms();
    return () => {
      if (before) queryClient.setQueryData(key, before);
      clearTransforms();
    };
  };

  const moveKit = useMutation({
    mutationFn: ({ kit, days }: { kit: PlanKit; days: number }) => shiftKit({ id: kit.id, revision: kit.revision }, addDays(kit.planned_start, days)),
    onMutate: ({ kit, days }) => {
      setError(null);
      setNotice(null);
      return { rollback: optimistic(new Set(kit.orders.map((o) => o.id)), days, kit.id) };
    },
    onSuccess: (r, { kit }) => {
      const pct = r.atp ? Number(r.atp.coverage_percentage) : null;
      setNotice(
        t("mp.kitMoved", { number: kit.number, days: r.days, moved: r.moved, skipped: r.skipped.length }) +
          (pct !== null ? ` ${t("mp.coverageNow", { pct: fmt.pct(Math.floor(pct * 10) / 10) })}` : "") +
          (r.violations.length ? ` ${t("kit.violationsLeft", { count: r.violations.length })}` : ""),
      );
      queryClient.setQueryData(kitKeys.detail(r.kit.id), r.kit);
    },
    onError: (e, _v, ctx) => {
      ctx?.rollback();
      setError(kitErrorMessage(t, e));
    },
    onSettled: refresh,
  });

  const moveOne = useMutation({
    mutationFn: ({ order, days, confirmed }: { order: PlanOrder; kit: PlanKit | null; days: number; confirmed: boolean }) =>
      moveOrder(order.id, order.revision, addDays(order.start_date, days), confirmed),
    onMutate: ({ order, kit, days }) => {
      setError(null);
      setNotice(null);
      const ids = kit ? new Set(kit.orders.filter((o) => isUnder(kit, o.id, order.id)).map((o) => o.id)) : new Set([order.id]);
      return { rollback: optimistic(ids, days, null) };
    },
    onSuccess: (r, { order, days }, ctx) => {
      if (r.requires_confirmation) {
        ctx?.rollback();
        setConfirm({ order, days, violations: r.violations });
        return;
      }
      setConfirm(null);
      setNotice(t("mp.orderMoved", { number: order.number, days: r.days, moved: r.moved }));
      if (r.kit) queryClient.setQueryData(kitKeys.detail(r.kit.id), r.kit);
    },
    onError: (e, _v, ctx) => {
      ctx?.rollback();
      setError(kitErrorMessage(t, e));
    },
    onSettled: refresh,
  });

  const select = (s: Selection) => {
    if (!s) return setSearch({ kit: undefined, order: undefined });
    if (s.kind === "kit") setSearch({ kit: s.id, order: undefined });
    else if (s.kitId) setSearch({ kit: s.kitId, order: undefined });
    else setSearch({ order: s.id, kit: undefined });
  };
  const selection: Selection = search.kit ? { kind: "kit", id: search.kit } : search.order ? { kind: "order", id: search.order } : null;
  const busy = moveKit.isPending || moveOne.isPending;

  return (
    <div className="flex min-h-0 grow flex-col gap-3">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.planning.title")} <span aria-hidden="true">/</span> {t("nav.planning.masterPlan")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("mp.title")}</h1>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label={t("mp.earlier")}
            onClick={() => setSearch({ from: addDays(search.from, -span.step) })}
            className="flex size-9 items-center justify-center rounded-xl border border-border bg-surface hover:bg-surface-hover"
          >
            <ChevronLeft size={16} />
          </button>
          <button type="button" onClick={() => setSearch({ from: mondayOf(todayIso()) })} className={secondaryButtonClass}>
            {t("mp.today")}
          </button>
          <button
            type="button"
            aria-label={t("mp.later")}
            onClick={() => setSearch({ from: addDays(search.from, span.step) })}
            className="flex size-9 items-center justify-center rounded-xl border border-border bg-surface hover:bg-surface-hover"
          >
            <ChevronRight size={16} />
          </button>
          <span className="tabular px-2 text-[13px] text-ink-soft">
            {fmt.day(w.from)} – {fmt.dayYear(w.to)}
          </span>
          <div role="radiogroup" aria-label={t("mp.zoom")} className="flex rounded-xl border border-border p-0.5">
            {(["day", "week"] as const).map((z) => (
              <button
                key={z}
                type="button"
                role="radio"
                aria-checked={search.zoom === z}
                onClick={() => setSearch({ zoom: z })}
                className={cn("h-8 rounded-lg px-3 text-[13px]", search.zoom === z ? "bg-ink font-medium text-surface" : "hover:bg-surface-hover")}
              >
                {t(`mp.zoom_${z}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {company && <RunBar companyId={company.id} />}
      {company && canMove && plan && plan.kits.length === 0 && (
        <PlanningExamples companyId={company.id} compact onLoaded={() => setSearch({ zoom: "week", from: mondayOf(todayIso()) })} />
      )}
      <Legend />
      <ServerError message={error} />
      {notice && (
        <p role="status" className="rounded-xl bg-surface-muted px-3 py-2 text-[13px]">
          {notice}
        </p>
      )}
      {confirm && (
        <div role="alertdialog" aria-labelledby="mp-confirm" className="flex flex-col gap-2 rounded-xl border border-(--status-warning-fg)/40 bg-(--status-warning-bg) p-3 text-[13px]">
          <p id="mp-confirm" className="flex items-center gap-2 font-medium">
            <AlertTriangle size={15} aria-hidden="true" />
            {t("kit.confirmTitle", { number: confirm.order.number })}
          </p>
          <ul className="list-disc pl-5">
            {confirm.violations.map((v) => (
              <li key={v.order_id}>
                {t("kit.violation", { number: v.number, due: fmt.day(v.due_date), parent: v.parent_number, start: fmt.day(v.parent_start), days: v.days, offset: v.offset_days })}
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              className={primaryButtonClass}
              disabled={busy}
              onClick={() => {
                const kit = plan?.kits.find((k) => k.orders.some((o) => o.id === confirm.order.id)) ?? null;
                moveOne.mutate({ order: confirm.order, kit, days: confirm.days, confirmed: true });
              }}
            >
              {t("kit.moveAnyway")}
            </button>
            <button type="button" className={secondaryButtonClass} onClick={() => setConfirm(null)}>
              {t("form.cancel")}
            </button>
          </div>
        </div>
      )}

      <div className="flex min-h-0 grow gap-4">
        <div ref={boardBoxRef} className="flex min-h-0 min-w-0 grow flex-col gap-2">
          {plan ? (
            rows.length === 0 ? (
              <div className="flex grow flex-col items-center justify-center gap-1 rounded-xl border border-border bg-surface p-8 text-center">
                <p className="font-medium">{t("mp.emptyTitle")}</p>
                <p className="text-[13px] text-ink-muted">{t("mp.emptyBody")}</p>
              </div>
            ) : (
              <MasterPlanBoard
                plan={plan}
                rows={rows}
                from={w.from}
                days={span.days}
                px={px}
                selection={selection}
                highlightKits={highlight}
                canMove={canMove && !busy}
                onSelect={select}
                onToggle={(id) =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
                onMoveKit={(kit, days) => moveKit.mutate({ kit, days })}
                onMoveOrder={(order, kit, days) => moveOne.mutate({ order, kit, days, confirmed: false })}
                scrollRef={boardRef}
                onScrollX={toStrip}
              />
            )
          ) : (
            <div className="grow animate-pulse rounded-xl border border-border bg-surface" aria-busy="true" />
          )}
          {(plan?.kits_has_more || plan?.loose_has_more) && <p className="text-xs text-ink-muted">{t("mp.truncated")}</p>}

          <div role="tablist" aria-label={t("mp.strip")} className="flex gap-1">
            {(["load", "components"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={search.strip === s}
                onClick={() => {
                  setComp(null);
                  setSearch({ strip: s });
                }}
                className={cn(
                  "h-8 rounded-lg px-3 text-[13px]",
                  search.strip === s ? "bg-surface font-medium shadow-sm ring-1 ring-border" : "text-ink-soft hover:bg-surface-hover",
                )}
              >
                {t(s === "load" ? "mp.loadStrip" : "mp.componentStrip")}
              </button>
            ))}
          </div>
          {search.strip === "load" ? (
            <LoadStrip load={loadQ.data} from={w.from} days={span.days} px={px} scrollRef={stripRef} onScrollX={toBoard} />
          ) : (
            <ComponentStrip
              data={compQ.data}
              from={w.from}
              days={span.days}
              px={px}
              selected={comp}
              onSelect={setComp}
              scrollRef={stripRef}
              onScrollX={toBoard}
            />
          )}
        </div>
        {(search.kit || search.order) && (
          <DetailPanel label={search.kit ? t("kit.detailLabel") : t("prd.detailLabel")} open placeholder={null}>
            {search.kit ? (
              <KitPanel key={search.kit} id={search.kit} companyId={company?.id ?? ""} onClose={() => select(null)} onOpen={(id) => setSearch({ kit: id })} />
            ) : (
              <ProductionOrderPanel key={search.order} id={search.order!} onClose={() => select(null)} onOpen={(id) => setSearch({ order: id })} />
            )}
          </DetailPanel>
        )}
      </div>
    </div>
  );
}

/** Whether `id` is `rootId` or below it in the kit's tree. */
function isUnder(kit: PlanKit, id: string, rootId: string): boolean {
  const parent = new Map(kit.orders.map((o) => [o.id, o.parent_order_id ?? null]));
  let cur: string | null = id;
  for (let i = 0; cur && i < 200; i++) {
    if (cur === rootId) return true;
    cur = parent.get(cur) ?? null;
  }
  return false;
}

function Legend() {
  const { t } = useTranslation();
  const dot = "inline-block size-2.5 rounded-sm";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft" aria-label={t("mp.legend")}>
      <span className="font-medium text-ink-muted">{t("mp.legendCoverage")}</span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-success-fg)")} /> 100 %
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-warning-fg)")} /> 80–99 %
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-danger-fg)")} /> {"< 80 %"}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block size-2.5 rotate-45 bg-(--status-danger-fg)" /> {t("mp.legendCtp")}
      </span>
      <span className="flex items-center gap-1.5">
        <Truck size={12} /> {t("mp.supplyArrives")}
      </span>
      <span className="font-medium text-ink-muted">{t("mp.legendLoad")}</span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-success-bg) ring-1 ring-border")} /> ≤ 85 %
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-warning-bg) ring-1 ring-border")} /> ≤ 100 %
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn(dot, "bg-(--status-danger-bg) ring-1 ring-border")} /> {"> 100 %"}
      </span>
      <span className="text-ink-muted">{t("mp.legendHint")}</span>
    </div>
  );
}
